/**
 * Central Color Palette for Siza_Water Application
 * Modern, professional color scheme for consistent UI design
 */

// Primary Color Palette
export const colors = {
  // Main brand colors
  ashGray: '#B0D4CD',
  pictonBlue: '#4EADE2', 
  cambridgeBlue: '#78A188',
  antiflashWhite: '#EAEFF3',
  steelBlue: '#0086D4',

  // Semantic colors derived from palette
  primary: '#0086D4',      // steel-blue - main action color
  secondary: '#78A188',    // cambridge-blue - secondary actions
  accent: '#4EADE2',       // picton-blue - highlights and emphasis
  light: '#EAEFF3',        // antiflash-white - backgrounds
  muted: '#B0D4CD',        // ash-gray - subtle elements

  // Status colors (harmonized with palette)
  success: '#78A188',      // cambridge-blue for approvals
  warning: '#4EADE2',      // picton-blue for attention
  danger: '#B0796D',       // adjusted red-tint from ash-gray
  info: '#4EADE2',         // picton-blue for information

  // Text colors
  textPrimary: '#2C3E50',
  textSecondary: '#78A188',
  textMuted: '#B0D4CD',
  textWhite: '#FFFFFF',

  // Background variations
  bgPrimary: '#EAEFF3',
  bgSecondary: '#F8F9FA',
  bgCard: '#FFFFFF',
  bgHover: '#B0D4CD'
};

// HSL values for more advanced color manipulation
export const colorsHSL = {
  ashGray: 'hsla(168, 30%, 76%, 1)',
  pictonBlue: 'hsla(201, 72%, 60%, 1)',
  cambridgeBlue: 'hsla(143, 18%, 55%, 1)',
  antiflashWhite: 'hsla(207, 27%, 94%, 1)',
  steelBlue: 'hsla(202, 100%, 42%, 1)'
};

// Gradient definitions
export const gradients = {
  primary: `linear-gradient(135deg, ${colors.steelBlue}, ${colors.pictonBlue})`,
  secondary: `linear-gradient(135deg, ${colors.cambridgeBlue}, ${colors.ashGray})`,
  accent: `linear-gradient(90deg, ${colors.pictonBlue}, ${colors.steelBlue})`,
  light: `linear-gradient(135deg, ${colors.antiflashWhite}, ${colors.light})`,
  
  // Card gradients
  cardHeader: `linear-gradient(135deg, ${colors.steelBlue}, ${colors.pictonBlue})`,
  cardSuccess: `linear-gradient(135deg, ${colors.cambridgeBlue}, ${colors.ashGray})`,
  cardWarning: `linear-gradient(135deg, ${colors.pictonBlue}, ${colors.steelBlue})`,
  cardInfo: `linear-gradient(135deg, ${colors.cambridgeBlue}, ${colors.steelBlue})`
};

// Button variants using the color palette
export const buttonVariants = {
  primary: {
    background: colors.steelBlue,
    color: colors.textWhite,
    border: colors.steelBlue,
    hover: {
      background: colors.pictonBlue,
      border: colors.pictonBlue
    }
  },
  secondary: {
    background: colors.cambridgeBlue,
    color: colors.textWhite,
    border: colors.cambridgeBlue,
    hover: {
      background: colors.ashGray,
      border: colors.ashGray,
      color: colors.textPrimary
    }
  },
  outline: {
    background: 'transparent',
    color: colors.steelBlue,
    border: colors.steelBlue,
    hover: {
      background: colors.steelBlue,
      color: colors.textWhite
    }
  },
  light: {
    background: colors.antiflashWhite,
    color: colors.textPrimary,
    border: colors.antiflashWhite,
    hover: {
      background: colors.ashGray,
      border: colors.ashGray
    }
  }
};

// Card color schemes
export const cardSchemes = {
  default: {
    header: gradients.cardHeader,
    body: colors.bgCard,
    border: colors.light
  },
  success: {
    header: gradients.cardSuccess,
    body: colors.bgCard,
    border: colors.cambridgeBlue
  },
  warning: {
    header: gradients.cardWarning,
    body: colors.bgCard,
    border: colors.pictonBlue
  },
  info: {
    header: gradients.cardInfo,
    body: colors.bgCard,
    border: colors.ashGray
  }
};

// Utility functions for color manipulation
export const colorUtils = {
  // Add transparency to any color
  withOpacity: (color, opacity) => {
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
  },
  
  // Get hover state for any color
  getHoverColor: (baseColor) => {
    switch(baseColor) {
      case colors.steelBlue: return colors.pictonBlue;
      case colors.pictonBlue: return colors.steelBlue;
      case colors.cambridgeBlue: return colors.ashGray;
      case colors.ashGray: return colors.cambridgeBlue;
      default: return baseColor;
    }
  }
};

export default colors;
